'use client';

import Script from 'next/script';

export type ThirdPartyScriptsProps = {
  googleTagManagerId?: string;
  nostoAccountId?: string;
  vwoAccountId?: string;
};

export const ThirdPartyScripts = ({
  googleTagManagerId,
  nostoAccountId,
  vwoAccountId,
}: ThirdPartyScriptsProps) => {
  return (
    <>
      {/* requestIdleCallback polyfill (safe, tiny) */}
      <Script id="ric-polyfill" strategy="afterInteractive">
        {`
          window.requestIdleCallback = window.requestIdleCallback || function (cb) {
            var start = Date.now();
            return setTimeout(function () {
              cb({
                didTimeout: false,
                timeRemaining: function () {
                  return Math.max(0, 50 - (Date.now() - start));
                },
              });
            }, 1);
          };
        `}
      </Script>

      {/* Google Tag Manager */}
      {googleTagManagerId && (
        <>
          <Script id="gtm-script" strategy="afterInteractive">
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.defer=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${googleTagManagerId}');
            `}
          </Script>
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        </>
      )}

      {/* Nosto */}
      {nostoAccountId && (
        <Script id="nosto-script" strategy="lazyOnload">
          {`
            function onNostroLoaded(){
              if (window.nostojs) {
                window.nostojs = window.nostojs || (function(o){ (window.nostojs.q = window.nostojs.q || []).push(o); });
                window.nostojs(function(api){ api.setAutoLoad(false); });
              } else {
                setTimeout(onNostroLoaded, 2000);
              }
            }
            function loadNostroScript(){
              setTimeout(function(){
                var head = document.getElementsByTagName("head")[0];
                var script = document.createElement("script");
                script.id = "NostroScript";
                script.defer = true;
                script.async = true;
                script.src = "//connect.nosto.com/include/${nostoAccountId}";
                head.appendChild(script);
                onNostroLoaded();
              }, 1000);
            }
            if (window.requestIdleCallback) window.requestIdleCallback(loadNostroScript);
            else setTimeout(loadNostroScript, 0);
          `}
        </Script>
      )}

      {/* VWO (Async SmartCode, minimized) */}
      {vwoAccountId && (
        <Script id="vwo-smartcode" strategy="afterInteractive">
          {`
            (function() {
              var account_id=${JSON.stringify(vwoAccountId)}, version=2.1, settings_tolerance=2000,
                  hide_element='body', hide_element_style='opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;';
              var f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};
              try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}
              var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;
              var code={
                nonce:v&&v.nonce,
                settings_tolerance:function(){return cc.sT||settings_tolerance},
                hide_element_style:function(){return '{'+(cc.hES||hide_element_style)+'}'},
                hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return ''}return typeof cc.hE==='string'?cc.hE:hide_element},
                finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},
                addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},
                load:function(e){var n=this.getSettings(),i=d.createElement('script'),r=this;if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);}
                  else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=true;o.responseType='text';
                    o.onload=function(){if(o.status===200||o.status===304){code.addScript({text:o.responseText})}else{code.finish('&e=loading_failure:'+e)}};
                    o.onerror=function(){code.finish('&e=loading_failure:'+e)};o.send()}
                },
                getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},
                init:function(){
                  if(d.URL.indexOf('__vwo_disable__')>-1)return;
                  var e=this.settings_tolerance();
                  w._vwo_settings_timer=setTimeout(function(){code.finish();stT.removeItem(cK)},e);
                  var t;
                  if(this.hide_element()!=='body'){
                    t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];
                    t.id='_vis_opt_path_hides';v&&t.setAttribute('nonce',v.nonce);t.type='text/css';
                    if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)
                  } else {
                    t=d.getElementsByTagName('head')[0];var i=d.createElement('div');
                    i.style.cssText='z-index:2147483647;position:fixed;left:0;top:0;width:100%;height:100%;background:white;display:block;';
                    i.id='_vis_opt_path_hides';i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)
                  }
                  var s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;
                  code.load(s+'&x=true');
                }
              };
              w._vwo_code = code; code.init();
            })();
          `}
        </Script>
      )}
    </>
  );
};
